import { useCallback, useEffect, useRef, useState } from 'react';
import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { AssistantConversation } from './AssistantConversation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type Report = {
  id: string;
  status: string;
  reason: string | null;
  total: number;
  starts_at: string;
  ends_at: string;
  counts: Record<string, number>;
  next_cursor: string | null;
  items: {
    id: string;
    name: string;
    document_id: string | null;
    revision_id: string | null;
    legacy_job_id?: string | null;
    status: string;
    reason: string | null;
  }[];
};
export function InboxPreparation({
  base,
  onChanged,
  onOpenSource,
  onReviewIntake,
  showConversation = true,
}: {
  showConversation?: boolean;
  base: string;
  onChanged: () => void;
  onReviewIntake?: (job: string, documentId: string | null) => void;
  onOpenSource: (source: { documentId: string; revisionId?: string }) => void;
}) {
  const [report, setReport] = useState<Report | null>(null);
  const [busy, setBusy] = useState(false);
  const [coverageError, setCoverageError] = useState('');
  const [error, setError] = useState('');
  const expanded = useRef(false);
  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const { data } = await clientApi.get<Report | null>(`${base}/sweeps/latest`, { signal });
        if (!signal?.aborted) {
          setReport(data);
          expanded.current = false;
          setCoverageError('');
        }
      } catch {
        if (!signal?.aborted) {
          setReport(null);
          expanded.current = false;
          setCoverageError('Coverage could not be loaded. Preparation status is unconfirmed.');
        }
      }
    },
    [base],
  );
  useEffect(() => {
    const abort = new AbortController();
    setReport(null);
    expanded.current = false;
    void refresh(abort.signal);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !expanded.current) void refresh(abort.signal);
    }, 10000);
    return () => {
      abort.abort();
      window.clearInterval(timer);
    };
  }, [refresh, base]);
  async function prepare(hours = 72) {
    setBusy(true);
    setError('');
    try {
      const { data } = await clientApi.post<Report>(`${base}/sweeps`, { request_id: crypto.randomUUID(), hours });
      setReport(data);
      expanded.current = false;
      onChanged();
    } catch {
      setError('Catch-up was not confirmed. Refresh coverage before retrying; a scan may already be running.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Prepare and review incoming documents</CardTitle>
        <CardDescription>
          Check uploaded documents received in a fixed time window. Preparation creates review tasks; it does not
          finalize charts or send messages.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {showConversation && (
          <AssistantConversation
            base={base}
            onChanged={() => {
              void refresh();
              onChanged();
            }}
          />
        )}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={busy || report?.status === 'scanning'} onClick={() => void prepare()}>
            Prepare past 72 hours
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => void refresh()}>
            Refresh coverage
          </Button>
        </div>
        {error && <p role="alert">{error}</p>}
        {coverageError && <p role="alert">{coverageError}</p>}
        {report ? (
          <div className="flex flex-col gap-3" aria-label="Document preparation coverage">
            <p>
              {new Date(report.starts_at).toLocaleString()} – {new Date(report.ends_at).toLocaleString()}
            </p>
            <p>
              {report.total} documents inventoried ·{' '}
              {report.status === 'scanning'
                ? 'Scan still running; totals are partial'
                : report.status === 'blocked'
                  ? 'Scan blocked; coverage is incomplete'
                  : 'Inventory scan finished'}
            </p>
            {report.reason && <p>{report.reason.replaceAll('_', ' ')}</p>}
            <p>
              {Object.entries(report.counts)
                .map(([key, count]) => `${count} ${key.replaceAll('_', ' ')}`)
                .join(' · ')}
            </p>
            <p className="text-sm text-muted-foreground">
              A finished scan does not mean every document is ready. Resolve blocked items and review prepared actions
              below. Coverage includes documents received by MohairNotes, not faxes missing at the provider.
            </p>
            {report.items.map((item) => (
              <div key={item.id} className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p>{item.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {item.status === 'ready' ? 'Prepared — review still required' : item.status.replaceAll('_', ' ')}
                    {item.reason ? ` · ${item.reason.replaceAll('_', ' ')}` : ''}
                  </p>
                </div>
                {['legacy_source_needs_review', 'legacy_patient_changed'].includes(item.reason ?? '') && (
                  <p className="text-sm">
                    Review the source and current patient association. If the old intake is unavailable, use Prepare
                    current document on its task below to create a fresh review draft.
                  </p>
                )}
                {item.reason === 'text_extraction_required' && (
                  <p className="text-sm">
                    Open the source to check readability and extraction status before preparing it again.
                  </p>
                )}
                {item.legacy_job_id && onReviewIntake && (
                  <Button onClick={() => onReviewIntake(item.legacy_job_id!, item.document_id)}>
                    Review intake here
                  </Button>
                )}
                {item.document_id && (
                  <Button
                    variant="outline"
                    onClick={() =>
                      onOpenSource({ documentId: item.document_id!, revisionId: item.revision_id ?? undefined })
                    }>
                    Open source
                  </Button>
                )}
              </div>
            ))}
            {expanded.current && (
              <p className="text-sm text-muted-foreground">
                Automatic refresh is paused while viewing additional documents. Refresh coverage to update results and
                return to the first page.
              </p>
            )}
            {report.next_cursor && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await clientApi.get<Report>(`${base}/sweeps/${report.id}`, {
                      params: { after: report.next_cursor },
                    });
                    expanded.current = true;
                    setReport((current) =>
                      current?.id === data.id ? { ...data, items: [...current.items, ...data.items] } : current,
                    );
                  } catch {
                    setError('More documents could not be loaded.');
                  }
                }}>
                Load more documents
              </Button>
            )}
          </div>
        ) : (
          <p>No catch-up scan yet. New documents continue through automatic preparation.</p>
        )}
      </CardContent>
    </Card>
  );
}
